import React, { FormEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { updateAddPhonePromptedAt, updateUserDetails } from 'actions/AuthActions'
import TextButton from 'LuxKit/Button/TextButton'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import LegalText from './AccountAccessLegalText'
import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { AccountAccessModalResult } from './AccountAccessModal'
import * as Analytics from 'analytics/analytics'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import StackedLoyaltyTierIcon from 'loyalty/components/StackedLoyaltyTierIcon'

interface PhoneNum {
  phone: string
  prefix: string
}

interface AddPhoneForm {
  phoneNum: PhoneNum
}

interface Props {
  error?: any
  regionCode?: string
  phone?: string
  dismissable?: boolean
  trackPromptImpression?: boolean
  hasDismissed?: boolean
  onDismissChange?: (dismissed: boolean) => void
}

// Phone number added points - A/B test variants
// Control: 0 points, Variant 1: 50 points, Variant 2: 500 points
const PHONE_POINTS_CONFIG = {
  control: { points: 0, earnKey: 'pt=all&c=phoneadded' },
  variant_1: { points: 50, earnKey: 'pt=all&c=phoneadded50' },
  variant_2: { points: 500, earnKey: 'pt=all&c=phoneadded500' },
} as const

const ADD_PHONE_OPTIMIZELY_EVENT = {
  id: '4586532951031808',
  key: 'add_phone_number',
} as const

function AccountAccessAddPhone(props: Props) {
  const {
    regionCode,
    phone,
    dismissable,
    trackPromptImpression = true,
    hasDismissed: hasDismissedProp,
    onDismissChange,
  } = props

  const termsCheckboxRef = useRef<HTMLInputElement>(null)
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const dispatch = useAppDispatch()
  const [internalHasDismissed, setInternalHasDismissed] = useState(false)
  const isDismissedControlled = typeof hasDismissedProp === 'boolean'
  const hasDismissed = isDismissedControlled ? Boolean(hasDismissedProp) : internalHasDismissed

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const experimentVariant = useOptimizelyExperiment(
    OptimizelyExperiments.croLoyaltyAddPhonePointsReward,
    isLoyaltyEnabled,
  )

  // Map variant to config (control if undefined or not in config)
  const phoneConfig = experimentVariant && PHONE_POINTS_CONFIG[experimentVariant as keyof typeof PHONE_POINTS_CONFIG]
    ? PHONE_POINTS_CONFIG[experimentVariant as keyof typeof PHONE_POINTS_CONFIG]
    : PHONE_POINTS_CONFIG.control

  const pointsReward = phoneConfig.points
  const phonePointsEarnKey = phoneConfig.earnKey

  // Hide points callout when 0 points (control variant)
  const showPointsEarnCallout = isLoyaltyEnabled && pointsReward > 0

  const setHasDismissed = useCallback((nextState: boolean) => {
    if (!isDismissedControlled) {
      setInternalHasDismissed(nextState)
    }
    onDismissChange?.(nextState)
  }, [isDismissedControlled, onDismissChange])

  const onAddPhone = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    const joinData = formToObject<AddPhoneForm>(event.currentTarget)
    dispatch(updateUserDetails(
      {
        phone: joinData.phoneNum.phone,
        phonePrefix: joinData.phoneNum.prefix,
      },
      null,
      isLoyaltyEnabled ? phonePointsEarnKey : undefined,
    ))
  }, [dispatch, isLoyaltyEnabled, phonePointsEarnKey])

  const onClose = useCallback(() => {
    if (hasDismissed) {
      return modalContext.resolve({ loggedIn: true })
    }

    setHasDismissed(true)
  }, [modalContext, hasDismissed, setHasDismissed])

  useEffect(() => {
    if (trackPromptImpression) {
      dispatch(updateAddPhonePromptedAt(new Date()))
    }
    Analytics.trackClientEvent({
      subject: 'add_phone_number',
      action: 'impression',
      category: 'logging',
      type: 'operational',
    })
  }, [dispatch, trackPromptImpression])

  useEffect(() => {
    if (phone?.length) {
      const optimizelyEvent = {
        optimizelyEventId: ADD_PHONE_OPTIMIZELY_EVENT.id,
        optimizelyEventKey: ADD_PHONE_OPTIMIZELY_EVENT.key,
      }

      Analytics.trackClientEvent({
        subject: 'add_phone_number',
        action: 'interaction',
        category: 'logging',
        type: 'operational',
        ...optimizelyEvent,
      })
      modalContext.resolve({ loggedIn: true })
    }
  }, [modalContext, phone])

  const title = useMemo(() => {
    if (hasDismissed) {
      return 'Are you sure?'
    }
    if (isLoyaltyEnabled && pointsReward > 0) {
      return `Secure your account with a mobile number and earn ${pointsReward.toLocaleString()} Société Points`
    }
    return 'Secure your account with a mobile number'
  }, [hasDismissed, isLoyaltyEnabled, pointsReward])

  return (
    <>
      <ModalHeader
        title={title}
        subtitle={!hasDismissed ? 'Link your mobile to your account for added security and a smoother, faster login experience.' : undefined}
        dismissible={dismissable}
        onCloseButtonClick={onClose}
      />
      <ModalBody>
        <ModalContent>
          <form name="joinForm" onSubmit={onAddPhone}>
            <Group direction="vertical" gap={12}>
              {hasDismissed && <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
                Adding your phone number is highly recommended to help keep your account safe
              </MessageBanner>}
              <Group direction="vertical" gap={4}>
                <PhoneInput
                  id="phoneNum"
                  name="phoneNum"
                  label="Phone number"
                  prefixCountry={regionCode}
                  required
                />
                {showPointsEarnCallout && !hasDismissed && (
                  <CaptionTextBlock
                    variant="medium"
                    colour="neutral-three"
                    startIcon={<StackedLoyaltyTierIcon />}
                  >
                    Earn {pointsReward.toLocaleString()} points for adding your phone number
                  </CaptionTextBlock>
                )}
              </Group>
              <CheckboxInput
                ref={termsCheckboxRef}
                name="agreeTandC"
                data-testid="terms-and-privacy-agree-checkbox"
                required
              >
                <LegalText modalType="promptAddPhone" />
              </CheckboxInput>
              <TextButton
                kind="primary"
                fit="flex"
                type="submit"
                size="large"
              >
                {hasDismissed ? 'Secure my account' : 'Add mobile number'}
              </TextButton>
            </Group>
          </form>
        </ModalContent>
      </ModalBody>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    regionCode: state.geo.currentRegionCode,
    phone: state.auth.account.phone,
  }
}

export default connect(mapStateToProps)(AccountAccessAddPhone)
